import type { TrustIDOAuthConfig } from "./types";
import { buildAuthorizationUrl } from "./oauth";
import { getStyles } from "./styles";

const SHIELD_SVG = `<svg viewBox="0 0 59 59" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M32.5138 39.1756L29.5178 42.1716L26.5218 39.1756L22.5837 35.2375L25.5797 32.2415L29.5178 36.1796L38.6397 27.0577H20.3423V36.6752C20.3423 40.8232 24.0169 44.3103 29.4999 45.8418C34.9829 44.3103 38.6576 40.8232 38.6576 36.6752V33.0318L32.5138 39.1756Z" fill="ICON_PRIMARY"/>
  <path d="M29.5 0C13.2074 0 0 13.2074 0 29.5C0 45.7926 13.2074 59 29.5 59C45.7926 59 59 45.7926 59 29.5C59 13.2074 45.7926 0 29.5 0ZM16.1096 14.8415V14.3906H34.4204V18.6234H16.1096V14.8415ZM42.8904 27.0577V36.6752C42.8904 42.8011 37.4253 48.302 29.5938 50.0567L29.4955 50.079L29.3973 50.0567C21.5702 48.3064 16.1007 42.8011 16.1007 36.6752V22.8249H42.8815V27.0577H42.8904ZM42.8904 14.8415V18.6234H38.6576V14.3906H42.8904V14.8415Z" fill="ICON_PRIMARY"/>
  <path d="M16.1096 27.0576V36.6751C16.1096 42.8011 21.5747 48.3019 29.4063 50.0566L29.5045 50.079L29.6027 50.0566C37.4298 48.3064 42.8994 42.8011 42.8994 36.6751V22.8248H16.1185V27.0576H16.1096ZM38.6577 36.6751C38.6577 40.8231 34.983 44.3102 29.5 45.8417C24.0171 44.3102 20.3424 40.8231 20.3424 36.6751V27.0576H38.6398L29.5179 36.1795L25.5798 32.2414L22.5838 35.2374L26.5219 39.1755L29.5179 42.1715L32.5139 39.1755L38.6577 33.0317V36.6751Z" fill="ICON_SECONDARY"/>
  <path d="M38.6577 18.6279H39.1087H42.8905V14.8416V14.3906H38.6577V18.6279Z" fill="ICON_SECONDARY"/>
  <path d="M34.4204 14.3906H16.1096V14.8416V18.6279H34.4204V14.3906Z" fill="ICON_SECONDARY"/>
</svg>`;

function getIconSvg(theme: "filled" | "outline"): string {
  const primary = theme === "filled" ? "#fff" : "#0E6FFF";
  const secondary = theme === "filled" ? "#0E6FFF" : "#fff";
  return SHIELD_SVG.replace(/ICON_PRIMARY/g, primary).replace(
    /ICON_SECONDARY/g,
    secondary
  );
}

export class TrustIDSignIn {
  private config: TrustIDOAuthConfig;
  private shadow: ShadowRoot | null = null;

  constructor(config: TrustIDOAuthConfig) {
    this.config = config;
    this.init();
  }

  private init(): void {
    const container = this.resolveContainer();
    if (!container) return;

    this.shadow = container.attachShadow({ mode: "open" });
    this.render();
  }

  private resolveContainer(): HTMLElement | null {
    const { container } = this.config;

    if (container instanceof HTMLElement) {
      return container;
    }

    if (typeof container === "string") {
      const el = document.querySelector<HTMLElement>(container);
      if (el) return el;
    }

    // Fallback: create a div after the current script tag
    const script = document.currentScript as HTMLScriptElement | null;
    const wrapper = document.createElement("div");
    if (script?.parentNode) {
      script.parentNode.insertBefore(wrapper, script.nextSibling);
    } else {
      document.body.appendChild(wrapper);
    }
    return wrapper;
  }

  private render(): void {
    if (!this.shadow) return;

    const theme = this.config.theme || "filled";
    const size = this.config.size || "standard";
    const width = this.config.width || "auto";
    const text = this.config.text || "Sign in with Trust ID";

    const style = document.createElement("style");
    style.textContent = getStyles(theme, size, width);
    this.shadow.appendChild(style);

    const button = document.createElement("button");
    button.className = "trustid-btn";
    button.type = "button";
    button.setAttribute("aria-label", text);
    button.innerHTML = `${getIconSvg(theme)}<span>${text}</span>`;
    button.addEventListener("click", () => this.handleClick());
    this.shadow.appendChild(button);
  }

  private async handleClick(): Promise<void> {
    try {
      const url = await buildAuthorizationUrl(this.config);
      window.location.href = url;
    } catch (err) {
      console.error("[TrustID OAuth] Failed to build authorization URL:", err);
    }
  }

  static init(
    config: TrustIDOAuthConfig,
    scriptEl?: HTMLScriptElement | null
  ): TrustIDSignIn {
    // Preserve currentScript reference for container resolution
    if (scriptEl && !config.container) {
      const wrapper = document.createElement("div");
      if (scriptEl.parentNode) {
        scriptEl.parentNode.insertBefore(wrapper, scriptEl.nextSibling);
      } else {
        document.body.appendChild(wrapper);
      }
      config = { ...config, container: wrapper };
    }
    return new TrustIDSignIn(config);
  }
}
